import { Dialog } from '@material-ui/core';
import React, { ReactElement, useState, useCallback } from 'react';
import FEATURES from 'src/featureMap';
import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import withAnalytics from 'src/lib/withAnalytics';
import EditUserDialog from 'src/routes/settings/users/EditUserDialog';
import UserSearch from 'src/routes/settings/users/UserSearch';
import UserTable from 'src/routes/settings/users/UserTable';
import { UserDetail } from 'src/types/dataTypes';
import { Alert, Box } from '@barracuda-internal/bds-core-v2';
import { usePageTitle } from 'src/hooks/useMastheadTitle';

export interface SearchProps {
	text: string;
	type: 'name' | 'email';
}

interface PaginatedResponse<T> {
	data: T[];
	count: number;
	totalCount: number;
}

function UserSettings(): ReactElement {
	usePageTitle('Users');
	const { auth } = useAuth();
	const { api } = useAppToolchain();

	const [doSearch, setDoSearch] = useState<SearchProps>();
	const [showEditDialog, setShowEditDialog] = useState<UserDetail>();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(25);

	const [searchStatus, usersResponse, , , setUsersResponse] = useApiRequest<PaginatedResponse<UserDetail>>(
		(signal) =>
			api.getAccountUsers(
				auth.accountDetails.account.id,
				doSearch?.text
					? {
							type: doSearch?.type,
							value: doSearch?.text,
					  }
					: null,
				{
					pageNumber: page - 1,
					pageSize,
				},
				{ signal },
			),
		[doSearch, page, pageSize],
	);

	const users = usersResponse?.data || [];
	const totalRecords = usersResponse?.totalCount || 0;

	const handleCloseEditUserDialog = () => {
		setShowEditDialog(null);
	};

	const handleChange = (user: UserDetail) => {
		const index = users.findIndex(
			(u) => u.user.id === showEditDialog.user.id,
		);
		if (index === -1) return;

		const updatedUsers = [...users];
		updatedUsers[index] = user;
		setUsersResponse({ data: updatedUsers, count: updatedUsers.length, totalCount: totalRecords });
	};

	const handleEditUser = (user: UserDetail) => setShowEditDialog(user);

	const handleSearch = withAnalytics(
		FEATURES.settingsUsersSearch,
		(search: SearchProps) => {
			setDoSearch(search);
			setPage(1);
		},
		(search: SearchProps) => ({
			searchType: search.type,
		}),
	);

	const handlePageChange = useCallback((newPage: number) => {
		setPage(newPage);
	}, []);

	const handlePageSizeChange = useCallback((newPageSize: number) => {
		setPageSize(newPageSize);
		setPage(1);
	}, []);

	return (
		<Box sx={{ pt: 4, px: 4 }}>
			{showEditDialog && (
				<Dialog
					maxWidth="lg"
					onClose={handleCloseEditUserDialog}
					open={!!showEditDialog}
				>
					<EditUserDialog
						onCancel={handleCloseEditUserDialog}
						onChange={handleChange}
						userDetail={showEditDialog}
					/>
				</Dialog>
			)}
			<Alert severity="info" sx={{ mb: 2 }}>
				User roles originate from Barracuda Cloud Control and are
				modifiable within the Barracuda Cloud Control System.
			</Alert>
			<UserSearch
				onSearch={handleSearch}
				isSearching={searchStatus === 'RUNNING' && !!doSearch}
			/>
			<Box sx={{ pt: 3 }}>
				<UserTable
					data={users}
					isLoading={searchStatus === 'RUNNING'}
					page={page}
					pageSize={pageSize}
					totalRecords={totalRecords}
					onEditUser={handleEditUser}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
				/>
			</Box>
		</Box>
	);
}

export default UserSettings;
